import { useEffect, useState } from 'react';
import { useMatch } from 'react-router-dom';
import { DashboardSection, TOOLKIT_PATH } from '@/components/Dashboard/enums/shared';
import {
  DASHBOARD_PAGE_STORAGE_KEY,
  DASHBOARD_SECTION_STORAGE_KEY,
  isDashboardPage,
  type DashboardPage,
} from '@/components/Dashboard/utils/dashboardSection.utils';
import { useToolTabsEnabled } from '@/hooks/useToolTabsEnabled';
import { useLocalStorage } from '@/hooks/useLocalStorage';

const DEFAULT_DASHBOARD_SECTION = DashboardSection.RecentRecipes;
const DEFAULT_DASHBOARD_PAGE: DashboardPage = 'workflow';

const DASHBOARD_SECTIONS = new Set(Object.values(DashboardSection));

const isDashboardSection = (value: string | null): value is DashboardSection => {
  return !!value && DASHBOARD_SECTIONS.has(value as DashboardSection);
};

export const useDashboardSection = () => {
  const areTabsEnabled = useToolTabsEnabled();
  const isToolkitPath = useMatch(TOOLKIT_PATH) !== null;
  const { getItem, setItem } = useLocalStorage<DashboardSection>(DASHBOARD_SECTION_STORAGE_KEY);
  const { getItem: getStoredPage, setItem: setStoredPage } = useLocalStorage<DashboardPage>(DASHBOARD_PAGE_STORAGE_KEY);

  const [storedSelection, setCurrentSelection] = useState<DashboardSection>(() => {
    const stored = getItem(false);
    return isDashboardSection(stored) ? stored : DEFAULT_DASHBOARD_SECTION;
  });

  const [storedPage, setCurrentPage] = useState<DashboardPage>(() => {
    const stored = getStoredPage(false);
    return isDashboardPage(stored) ? stored : DEFAULT_DASHBOARD_PAGE;
  });

  /**
   * A stored "Tools" selection (the old Apps row) outlives the nav that made it. With the kind pages
   * on, that row is the Tools page's Shared with me tab. Resolved while rendering so the empty Apps
   * section is never painted, then written back so it does not outlive this visit.
   */
  const opensToolsShared = areTabsEnabled && storedSelection === DashboardSection.SharedApps;
  const restoredSelection = opensToolsShared ? DashboardSection.SharedWithMe : storedSelection;
  const restoredPage = opensToolsShared ? 'tool' : storedPage;

  useEffect(() => {
    if (!opensToolsShared) return;
    setCurrentSelection(DashboardSection.SharedWithMe);
    setCurrentPage('tool');
  }, [opensToolsShared]);

  useEffect(() => {
    setItem(restoredSelection, false);
  }, [restoredSelection, setItem]);

  useEffect(() => {
    if (!areTabsEnabled) return;
    setStoredPage(restoredPage, false);
  }, [areTabsEnabled, restoredPage, setStoredPage]);

  return {
    currentSelection: isToolkitPath ? DashboardSection.Toolkit : restoredSelection,
    setCurrentSelection,
    currentPage: restoredPage,
    setCurrentPage,
  };
};
